/**
 * MapView Component - Sistema GIS Terranet
 * Fecha: 28 Enero 2026
 * Versión: MEJORADA con filtros, edición y colores correctos
 */

import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, Polyline, useMap, useMapEvents, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import '../styles/MapViewAdvanced.css';

const { BaseLayer, Overlay } = LayersControl;

// Fix iconos Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Icono personalizado para búsqueda
const searchIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Icono para puntos de medición
const measureIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [20, 33],
  iconAnchor: [10, 33],
  popupAnchor: [1, -28],
  shadowSize: [33, 33]
});

// Colores de fibra según nomenclatura exacta
const COLORES_FIBRA = {
  '48': { nombre: '48 Hilos - Azul', color: '#0000FF' },
  '24': { nombre: '24 Hilos - Rojo', color: '#FF0000' },
  '12': { nombre: '12 Hilos - Naranja', color: '#FFA500' },
  '8': { nombre: '8 Hilos - Verde', color: '#00FF00' },
  '6': { nombre: '6 Hilos - Celeste', color: '#00FFFF' },
  '4': { nombre: '4 Hilos - Amarillo', color: '#FFFF00' },
  '2': { nombre: '2 Hilos - Fucsia', color: '#FF00FF' },
  '1': { nombre: '1 Hilo - Blanco', color: '#FFFFFF' }
};

// Función para obtener color de fibra basado en el nombre/tipo
const obtenerColorFibra = (nombre) => {
  if (!nombre) return '#5f27cd';
  const nombreUpper = nombre.toUpperCase();

  // Buscar patrón como "48_H_AZUL", "24_H_ROJO", etc.
  if (nombreUpper.includes('48') || nombreUpper.includes('AZUL')) return '#0000FF';
  if (nombreUpper.includes('24') || nombreUpper.includes('ROJO')) return '#FF0000';
  if (nombreUpper.includes('12') || nombreUpper.includes('NARANJA')) return '#FFA500';
  if (nombreUpper.includes('8_H') || nombreUpper.includes('VERDE')) return '#00FF00';
  if (nombreUpper.includes('6_H') || nombreUpper.includes('CELESTE')) return '#00FFFF';
  if (nombreUpper.includes('4_H') || nombreUpper.includes('AMARILLO')) return '#FFFF00';
  if (nombreUpper.includes('2_H') || nombreUpper.includes('FUCSIA')) return '#FF00FF';
  if (nombreUpper.includes('1_H') || nombreUpper.includes('BLANCO')) return '#FFFFFF';

  return '#5f27cd'; // Color por defecto (morado)
};

// Función para obtener nombre del color
const obtenerNombreColor = (nombre) => {
  if (!nombre) return 'Sin definir';
  const nombreUpper = nombre.toUpperCase();

  if (nombreUpper.includes('48') || nombreUpper.includes('AZUL')) return '48H - Azul';
  if (nombreUpper.includes('24') || nombreUpper.includes('ROJO')) return '24H - Rojo';
  if (nombreUpper.includes('12') || nombreUpper.includes('NARANJA')) return '12H - Naranja';
  if (nombreUpper.includes('8_H') || nombreUpper.includes('VERDE')) return '8H - Verde';
  if (nombreUpper.includes('6_H') || nombreUpper.includes('CELESTE')) return '6H - Celeste';
  if (nombreUpper.includes('4_H') || nombreUpper.includes('AMARILLO')) return '4H - Amarillo';
  if (nombreUpper.includes('2_H') || nombreUpper.includes('FUCSIA')) return '2H - Fucsia';
  if (nombreUpper.includes('1_H') || nombreUpper.includes('BLANCO')) return '1H - Blanco';

  return 'Otro';
};

// Generar colores únicos para zonas
const generarColoresZonas = (totalZonas) => {
  const colores = [];
  for (let i = 0; i < totalZonas; i++) {
    const hue = (i * 360 / totalZonas) % 360;
    colores.push(`hsl(${hue}, 70%, 50%)`);
  }
  return colores;
};

// Componente para controlar centro del mapa
const MapController = ({ center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 13);
    }
  }, [center, zoom, map]);

  return null;
};

// Componente para manejar clics en el mapa (medición) - SIEMPRE ACTIVO cuando mide
const MapClickHandler = ({ onClick, activo }) => {
  useMapEvents({
    click: (e) => {
      if (activo && onClick) {
        onClick(e.latlng);
      }
    }
  });
  return null;
};

// Componente para mostrar coordenadas del mouse
const MouseCoordinates = ({ onMouseMove }) => {
  useMapEvents({
    mousemove: (e) => {
      onMouseMove(e.latlng);
    }
  });
  return null;
};

const MapView = ({ user }) => {
  // Debug: verificar que user se está pasando correctamente
  console.log('MapView - Usuario recibido:', user);
  console.log('MapView - Es Admin?:', user?.role === 'Administrador' || user?.role === 'admin');

  // Estados principales
  const [zonasGeojson, setZonasGeojson] = useState(null);
  const [cajasGeojson, setCajasGeojson] = useState(null);
  const [fibrasGeojson, setFibrasGeojson] = useState(null);
  const [fibrasVersion, setFibrasVersion] = useState(0); // Para forzar re-render del mapa
  const [capasActivas] = useState({
    zonas: true,
    cajas: true,
    fibras: true
  });

  // Estados navegación
  const [latitudBusqueda, setLatitudBusqueda] = useState('');
  const [longitudBusqueda, setLongitudBusqueda] = useState('');
  const [marcadorBusqueda, setMarcadorBusqueda] = useState(null);
  const [centroMapa, setCentroMapa] = useState([-2.1894, -79.8890]);
  const [zoomMapa, setZoomMapa] = useState(12);

  // Estados medición
  const [midiendoDistancia, setMidiendoDistancia] = useState(false);
  const [puntosMedicion, setPuntosMedicion] = useState([]);
  const [distanciaTotal, setDistanciaTotal] = useState(0);

  // Estados filtro zonas
  const [zonas, setZonas] = useState([]);
  const [zonasExpandidas, setZonasExpandidas] = useState({});
  const [elementosPorZona, setElementosPorZona] = useState({});

  // Estados edición
  const [editandoCaja, setEditandoCaja] = useState(null);
  const [datosEdicion, setDatosEdicion] = useState({});
  const [mostrarModalEdicion, setMostrarModalEdicion] = useState(false);

  // Estados edición fibra
  const [editandoFibra, setEditandoFibra] = useState(null);
  const [datosEdicionFibra, setDatosEdicionFibra] = useState({});
  const [mostrarModalFibra, setMostrarModalFibra] = useState(false);

  const isAdmin = user?.role === 'Administrador';
  const [coloresZonas, setColoresZonas] = useState([]);
  const mapRef = useRef(null);

  // Estado para coordenadas del mouse
  const [coordenadasMouse, setCoordenadasMouse] = useState({ lat: 0, lng: 0 });

  // ==================== CARGA DATOS ====================

  useEffect(() => {
    cargarDatos();
    cargarZonas();
  }, []);

  const cargarDatos = async () => {
    try {
      const zonasRes = await axios.get('/api/zonas/geometrias');
      setZonasGeojson(zonasRes.data);
      const totalZonas = zonasRes.data.features.length;
      setColoresZonas(generarColoresZonas(totalZonas));

      const cajasRes = await axios.get('/api/cajas/geojson');
      setCajasGeojson(cajasRes.data);

      const fibrasRes = await axios.get('/api/fibras/geojson');
      setFibrasGeojson(fibrasRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  };

  const cargarZonas = async () => {
    try {
      const response = await axios.get('/api/zonas');
      setZonas(response.data.zonas || []);
    } catch (error) {
      console.error('Error cargando zonas:', error);
    }
  };

  // Cargar elementos de una zona (cajas y fibras)
  const cargarElementosZona = async (zonaId) => {
    try {
      const [cajasRes, fibrasRes] = await Promise.all([
        axios.get(`/api/zonas/${zonaId}/cajas`),
        axios.get(`/api/zonas/${zonaId}/fibras`).catch(() => ({ data: { fibras: [] } }))
      ]);

      setElementosPorZona(prev => ({
        ...prev,
        [zonaId]: {
          cajas: cajasRes.data.cajas || [],
          fibras: fibrasRes.data.fibras || []
        }
      }));
    } catch (error) {
      console.error('Error cargando elementos de zona:', error);
      setElementosPorZona(prev => ({
        ...prev,
        [zonaId]: { cajas: [], fibras: [] }
      }));
    }
  };

  // ==================== NAVEGACIÓN ====================

  const buscarCoordenadas = () => {
    const lat = parseFloat(latitudBusqueda);
    const lng = parseFloat(longitudBusqueda);

    if (isNaN(lat) || isNaN(lng)) {
      alert('Por favor ingresa coordenadas válidas');
      return;
    }

    setCentroMapa([lat, lng]);
    setZoomMapa(16);
    setMarcadorBusqueda([lat, lng]);
  };

  const limpiarBusqueda = () => {
    setLatitudBusqueda('');
    setLongitudBusqueda('');
    setMarcadorBusqueda(null);
  };

  const centrarEnZona = (zonaId) => {
    if (zonasGeojson && zonasGeojson.features) {
      // Buscar por id_zona o id en properties
      const zonaFeature = zonasGeojson.features.find(
        f => f.properties.id_zona === zonaId || f.id === zonaId || f.properties.id === zonaId
      );

      if (zonaFeature && zonaFeature.geometry) {
        const coords = zonaFeature.geometry.coordinates;
        let latSum = 0, lngSum = 0, count = 0;

        const procesarCoords = (coordArray) => {
          if (typeof coordArray[0] === 'number') {
            lngSum += coordArray[0];
            latSum += coordArray[1];
            count++;
          } else {
            coordArray.forEach(procesarCoords);
          }
        };

        procesarCoords(coords);

        if (count > 0) {
          setCentroMapa([latSum / count, lngSum / count]);
          setZoomMapa(15);
        }
      }
    }
  };

  const toggleZonaExpansion = async (zonaId) => {
    if (zonasExpandidas[zonaId]) {
      setZonasExpandidas({ ...zonasExpandidas, [zonaId]: false });
    } else {
      if (!elementosPorZona[zonaId]) {
        await cargarElementosZona(zonaId);
      }
      setZonasExpandidas({ ...zonasExpandidas, [zonaId]: true });
    }
  };

  const centrarEnCaja = (lat, lng) => {
    setCentroMapa([lat, lng]);
    setZoomMapa(18);
  };

  const centrarEnFibra = (fibra) => {
    if (fibra.latitud && fibra.longitud) {
      setCentroMapa([fibra.latitud, fibra.longitud]);
      setZoomMapa(17);
    }
  };

  // ==================== MEDICIÓN ====================

  const iniciarMedicion = () => {
    setMidiendoDistancia(true);
    setPuntosMedicion([]);
    setDistanciaTotal(0);
  };

  const finalizarMedicion = () => {
    setMidiendoDistancia(false);
  };

  const limpiarMedicion = () => {
    setPuntosMedicion([]);
    setDistanciaTotal(0);
    setMidiendoDistancia(false);
  };

  const retrocederPunto = () => {
    if (puntosMedicion.length > 0) {
      const nuevosPuntos = puntosMedicion.slice(0, -1);
      setPuntosMedicion(nuevosPuntos);
      setDistanciaTotal(calcularDistanciaTotal(nuevosPuntos));
    }
  };

  const manejarClicMapa = (latlng) => {
    const nuevosPuntos = [...puntosMedicion, [latlng.lat, latlng.lng]];
    setPuntosMedicion(nuevosPuntos);
    setDistanciaTotal(calcularDistanciaTotal(nuevosPuntos));
  };

  const calcularDistanciaTotal = (puntos) => {
    if (puntos.length < 2) return 0;
    let distancia = 0;
    for (let i = 0; i < puntos.length - 1; i++) {
      const p1 = L.latLng(puntos[i]);
      const p2 = L.latLng(puntos[i + 1]);
      distancia += p1.distanceTo(p2);
    }
    return distancia;
  };

  // ==================== EDICIÓN ====================

  const iniciarEdicion = (caja) => {
    setEditandoCaja(caja.id_nap || caja.id);
    setDatosEdicion({
      nombre: caja.nombre || '',
      tipo: caja.tipo || '',
      subtipo: caja.subtipo || '',
      splitter: caja.splitter || '',
      capacidad_puertos: caja.capacidad_puertos || 8,
      puertos_ocupados: caja.puertos_ocupados || 0,
      estado: caja.estado || 'operativo'
    });
    setMostrarModalEdicion(true);
  };

  const guardarEdicion = async () => {
    try {
      await axios.put(
        `/api/cajas/${editandoCaja}`,
        datosEdicion
      );
      await cargarDatos();
      setMostrarModalEdicion(false);
      setEditandoCaja(null);
      setDatosEdicion({});
      alert('Caja actualizada correctamente');
    } catch (error) {
      console.error('Error guardando:', error);
      alert('Error al guardar los cambios');
    }
  };

  const cancelarEdicion = () => {
    setMostrarModalEdicion(false);
    setEditandoCaja(null);
    setDatosEdicion({});
  };

  // ==================== EDICIÓN FIBRA ====================

  const iniciarEdicionFibra = (fibra) => {
    setEditandoFibra(fibra.id_fibra || fibra.id);
    setDatosEdicionFibra({
      nombre: fibra.nombre || '',
      tipo: fibra.tipo || '',
      capacidad_hilos: fibra.capacidad_hilos || 0,
      hilos_ocupados: fibra.hilos_ocupados || 0,
      estado: fibra.estado || 'operativo'
    });
    setMostrarModalFibra(true);
  };

  const guardarEdicionFibra = async () => {
    try {
      console.log('Guardando fibra ID:', editandoFibra);
      console.log('Datos a enviar:', datosEdicionFibra);

      const response = await axios.put(
        `/api/fibras/${editandoFibra}`,
        datosEdicionFibra
      );

      console.log('Respuesta del servidor:', response.data);

      // Recargar datos para reflejar cambios
      await cargarDatos();

      // Forzar re-render del componente GeoJSON
      setFibrasVersion(prev => prev + 1);

      setMostrarModalFibra(false);
      setEditandoFibra(null);
      setDatosEdicionFibra({});
      alert('Fibra actualizada correctamente. El color se actualizará en el mapa.');
    } catch (error) {
      console.error('Error guardando fibra:', error);
      console.error('Detalles del error:', error.response?.data);
      alert('Error al guardar los cambios de la fibra: ' + (error.response?.data?.detail || error.message));
    }
  };

  const cancelarEdicionFibra = () => {
    setMostrarModalFibra(false);
    setEditandoFibra(null);
    setDatosEdicionFibra({});
  };

  // Exponer funciones globalmente para popups
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.iniciarEdicionCaja = iniciarEdicion;
    window.iniciarEdicionFibra = iniciarEdicionFibra;
    window.centrarEnCaja = centrarEnCaja;
    window.user = user;
    window.isAdmin = isAdmin;
  }, [user, isAdmin]);

  // ==================== ESTILOS Y POPUPS ====================

  const obtenerEstiloZona = (feature) => {
    const index = zonasGeojson.features.findIndex(
      f => f.properties.id_zona === feature.properties.id_zona
    );
    return {
      fillColor: coloresZonas[index] || '#3388ff',
      weight: 2,
      opacity: 1,
      color: '#ffffff',
      fillOpacity: 0.3,
      // Permitir clics a través de las zonas cuando se está midiendo
      interactive: !midiendoDistancia
    };
  };

  const onEachZona = (feature, layer) => {
    const props = feature.properties;
    // Usar campo 'name' que es el nombre real de la zona en la BD
    const nombreZona = props.name || props.nombre || props.zona_nombre || 'Sin nombre';

    const popupContent = `
      <div class="popup-zona">
        <h3 class="popup-title zona-title">${nombreZona}</h3>
        <div class="popup-content">
          <p><span class="popup-label">Código:</span> <span class="popup-value">${props.codigo || 'N/A'}</span></p>
          <p><span class="popup-label">Área:</span> <span class="popup-value">${props.area_km2 ? props.area_km2.toFixed(2) + ' km²' : 'N/A'}</span></p>
          <p><span class="popup-label">Descripción:</span> <span class="popup-value">${props.descripcion || props.description || 'N/A'}</span></p>
        </div>
      </div>
    `;
    layer.bindPopup(popupContent, { className: 'custom-popup' });
  };

  const obtenerEstiloCaja = () => {
    return {
      radius: 8,
      fillColor: '#f7b731',
      color: '#000',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.9
    };
  };

  const onEachCaja = (feature, layer) => {
    const props = feature.properties;
    const coords = feature.geometry.coordinates;

    // Verificar si es admin (role puede ser 'Administrador' o 'admin')
    const esAdminCaja = user?.role === 'Administrador' || user?.role === 'admin' || user?.rol === 'Administrador' || user?.rol === 'admin';
    const editButton = esAdminCaja
      ? `<button onclick="window.iniciarEdicionCaja(${JSON.stringify(props).replace(/"/g, '&quot;')})" class="btn-editar-popup">Editar</button>`
      : '';

    const popupContent = `
      <div class="popup-caja">
        <h3 class="popup-title caja-title">${props.nombre || 'Sin nombre'}</h3>
        <div class="popup-content">
          <div class="popup-row">
            <span class="popup-label">Código SN:</span>
            <span class="popup-value">${props.codigo || 'N/A'}</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Tipo:</span>
            <span class="popup-value">${props.tipo || 'N/A'}</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Subtipo:</span>
            <span class="popup-value">${props.subtipo || 'N/A'}</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Splitter:</span>
            <span class="popup-value">${props.splitter || 'N/A'}</span>
          </div>
          <div class="popup-divider"></div>
          <div class="popup-row">
            <span class="popup-label">Capacidad:</span>
            <span class="popup-value highlight">${props.capacidad_puertos || 8} puertos</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Ocupados:</span>
            <span class="popup-value">${props.puertos_ocupados || 0} puertos</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Disponibles:</span>
            <span class="popup-value success">${(props.capacidad_puertos || 8) - (props.puertos_ocupados || 0)} puertos</span>
          </div>
          <div class="popup-divider"></div>
          <div class="popup-row">
            <span class="popup-label">Estado:</span>
            <span class="popup-value estado-${(props.estado || 'operativo').toLowerCase()}">${props.estado || 'N/A'}</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Zona:</span>
            <span class="popup-value">${props.zona_nombre || 'Sin asignar'}</span>
          </div>
          <div class="popup-coords">
            <small>Lat: ${coords[1].toFixed(6)} | Lng: ${coords[0].toFixed(6)}</small>
          </div>
          ${editButton}
        </div>
      </div>
    `;
    layer.bindPopup(popupContent, { className: 'custom-popup', maxWidth: 350 });
  };

  const obtenerEstiloFibra = (feature) => {
    // Priorizar tipo sobre nombre para obtener el color correcto
    const color = obtenerColorFibra(feature.properties.tipo || feature.properties.nombre);
    return {
      color: color,
      weight: 4,
      opacity: 0.9
    };
  };

  const onEachFibra = (feature, layer) => {
    const props = feature.properties;
    // Priorizar tipo sobre nombre para obtener el color correcto
    const colorFibra = obtenerColorFibra(props.tipo || props.nombre);
    const nombreColor = obtenerNombreColor(props.tipo || props.nombre);

    // Verificar si es admin (role puede ser 'Administrador' o 'admin')
    const esAdmin = user?.role === 'Administrador' || user?.role === 'admin' || user?.rol === 'Administrador' || user?.rol === 'admin';
    console.log('onEachFibra - user:', user, 'esAdmin:', esAdmin);
    const editButton = esAdmin
      ? `<button onclick="window.iniciarEdicionFibra(${JSON.stringify(props).replace(/"/g, '&quot;')})" class="btn-editar-popup">Editar Fibra</button>`
      : '';

    const popupContent = `
      <div class="popup-fibra">
        <h3 class="popup-title fibra-title">${props.nombre || 'Sin nombre'}</h3>
        <div class="popup-content">
          <div class="popup-row">
            <span class="popup-label">Código:</span>
            <span class="popup-value">${props.codigo || 'N/A'}</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Tipo:</span>
            <span class="popup-value">${props.tipo || 'N/A'}</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Color:</span>
            <span class="popup-value">
              <span class="color-indicator" style="background:${colorFibra}; border:2px solid #333;"></span>
              ${nombreColor}
            </span>
          </div>
          <div class="popup-divider"></div>
          <div class="popup-row">
            <span class="popup-label">Longitud:</span>
            <span class="popup-value">${props.longitud_metros ? props.longitud_metros.toFixed(2) + ' m' : 'N/A'}</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Capacidad:</span>
            <span class="popup-value">${props.capacidad_hilos || 0} hilos</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Ocupados:</span>
            <span class="popup-value">${props.hilos_ocupados || 0} hilos</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Estado:</span>
            <span class="popup-value estado-${(props.estado || 'operativo').toLowerCase()}">${props.estado || 'N/A'}</span>
          </div>
          <div class="popup-row">
            <span class="popup-label">Zona:</span>
            <span class="popup-value">${props.zona_nombre || 'Sin asignar'}</span>
          </div>
          ${editButton}
        </div>
      </div>
    `;
    layer.bindPopup(popupContent, { className: 'custom-popup', maxWidth: 350 });
  };

  // ==================== RENDER ====================

  return (
    <div className="map-view-container">
      {/* Modal de Edición Fibra */}
      {mostrarModalFibra && (
        <div className="modal-overlay">
          <div className="modal-edicion">
            <h3>Editar Fibra</h3>
            <div className="form-edicion">
              <div className="form-group">
                <label>Nombre:</label>
                <input
                  type="text"
                  value={datosEdicionFibra.nombre}
                  onChange={(e) => setDatosEdicionFibra({...datosEdicionFibra, nombre: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Tipo/Color de Fibra:</label>
                <select
                  value={datosEdicionFibra.tipo}
                  onChange={(e) => setDatosEdicionFibra({...datosEdicionFibra, tipo: e.target.value})}
                >
                  <option value="">Seleccionar tipo...</option>
                  <option value="48_H_AZUL">48 Hilos - Azul</option>
                  <option value="24_H_ROJO">24 Hilos - Rojo</option>
                  <option value="12_H_NARANJA">12 Hilos - Naranja</option>
                  <option value="8_H_VERDE">8 Hilos - Verde</option>
                  <option value="6_H_CELESTE">6 Hilos - Celeste</option>
                  <option value="4_H_AMARILLO">4 Hilos - Amarillo</option>
                  <option value="2_H_FUCSIA">2 Hilos - Fucsia</option>
                  <option value="1_H_BLANCO">1 Hilo - Blanco</option>
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Capacidad (hilos):</label>
                  <input
                    type="number"
                    value={datosEdicionFibra.capacidad_hilos}
                    onChange={(e) => setDatosEdicionFibra({...datosEdicionFibra, capacidad_hilos: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="form-group">
                  <label>Hilos ocupados:</label>
                  <input
                    type="number"
                    value={datosEdicionFibra.hilos_ocupados}
                    onChange={(e) => setDatosEdicionFibra({...datosEdicionFibra, hilos_ocupados: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Estado:</label>
                <select
                  value={datosEdicionFibra.estado}
                  onChange={(e) => setDatosEdicionFibra({...datosEdicionFibra, estado: e.target.value})}
                >
                  <option value="operativo">Operativo</option>
                  <option value="mantenimiento">En Mantenimiento</option>
                  <option value="fuera_servicio">Fuera de Servicio</option>
                </select>
              </div>
              <div className="form-buttons">
                <button className="btn-guardar" onClick={guardarEdicionFibra}>
                  Guardar Cambios
                </button>
                <button className="btn-cancelar" onClick={cancelarEdicionFibra}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición Caja */}
      {mostrarModalEdicion && (
        <div className="modal-overlay">
          <div className="modal-edicion">
            <h3>Editar Caja NAP</h3>
            <div className="form-edicion">
              <div className="form-group">
                <label>Nombre:</label>
                <input
                  type="text"
                  value={datosEdicion.nombre}
                  onChange={(e) => setDatosEdicion({...datosEdicion, nombre: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Tipo:</label>
                <input
                  type="text"
                  value={datosEdicion.tipo}
                  onChange={(e) => setDatosEdicion({...datosEdicion, tipo: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Subtipo:</label>
                <input
                  type="text"
                  value={datosEdicion.subtipo}
                  onChange={(e) => setDatosEdicion({...datosEdicion, subtipo: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Splitter:</label>
                <input
                  type="text"
                  value={datosEdicion.splitter}
                  onChange={(e) => setDatosEdicion({...datosEdicion, splitter: e.target.value})}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Capacidad (puertos):</label>
                  <input
                    type="number"
                    value={datosEdicion.capacidad_puertos}
                    onChange={(e) => setDatosEdicion({...datosEdicion, capacidad_puertos: parseInt(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label>Puertos ocupados:</label>
                  <input
                    type="number"
                    value={datosEdicion.puertos_ocupados}
                    onChange={(e) => setDatosEdicion({...datosEdicion, puertos_ocupados: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Estado:</label>
                <select
                  value={datosEdicion.estado}
                  onChange={(e) => setDatosEdicion({...datosEdicion, estado: e.target.value})}
                >
                  <option value="operativo">Operativo</option>
                  <option value="mantenimiento">En Mantenimiento</option>
                  <option value="fuera_servicio">Fuera de Servicio</option>
                </select>
              </div>
              <div className="form-buttons">
                <button className="btn-guardar" onClick={guardarEdicion}>
                  Guardar Cambios
                </button>
                <button className="btn-cancelar" onClick={cancelarEdicion}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="map-grid">
        {/* SIDEBAR IZQUIERDO */}
        <div className="sidebar sidebar-left">
          {/* Coordenadas del mouse en tiempo real */}
          <div className="sidebar-section coordenadas-mouse">
            <h3>Posición del Cursor</h3>
            <div className="coords-display">
              <div className="coord-row">
                <span className="coord-label">Lat:</span>
                <span className="coord-value">{coordenadasMouse.lat.toFixed(6)}</span>
              </div>
              <div className="coord-row">
                <span className="coord-label">Lng:</span>
                <span className="coord-value">{coordenadasMouse.lng.toFixed(6)}</span>
              </div>
            </div>
          </div>

          {/* Filtro por Zona - Estilo Google Earth */}
          <div className="sidebar-section zonas-section">
            <h3>Explorar Zonas ({zonas.length})</h3>
            <div className="zonas-lista">
              {zonas.length > 0 ? (
                zonas.map((zona) => (
                  <div key={zona.id} className={`zona-item ${zonasExpandidas[zona.id] ? 'expandida' : ''}`}>
                    <div className="zona-header" onClick={() => centrarEnZona(zona.id)}>
                      <button
                        className="zona-expand-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleZonaExpansion(zona.id);
                        }}
                      >
                        {zonasExpandidas[zona.id] ? '▼' : '▶'}
                      </button>
                      <span className="zona-icon">{zonasExpandidas[zona.id] ? '📂' : '📁'}</span>
                      <span className="zona-nombre">{zona.nombre || zona.codigo || `Zona ${zona.id}`}</span>
                    </div>
                    {zonasExpandidas[zona.id] && elementosPorZona[zona.id] && (
                      <div className="elementos-zona">
                        {/* Cajas NAP */}
                        {elementosPorZona[zona.id].cajas.length > 0 && (
                          <div className="elementos-grupo">
                            <div className="grupo-titulo">
                              <span className="grupo-icon">📦</span>
                              Cajas NAP ({elementosPorZona[zona.id].cajas.length})
                            </div>
                            <div className="elementos-lista">
                              {elementosPorZona[zona.id].cajas.slice(0, 20).map((caja) => (
                                <div
                                  key={caja.id}
                                  className="elemento-item caja"
                                  onClick={() => centrarEnCaja(caja.latitud, caja.longitud)}
                                  title={`Lat: ${caja.latitud}, Lng: ${caja.longitud}`}
                                >
                                  <span className="elemento-nombre">{caja.nombre || 'Sin nombre'}</span>
                                </div>
                              ))}
                              {elementosPorZona[zona.id].cajas.length > 20 && (
                                <div className="mas-elementos">
                                  ... y {elementosPorZona[zona.id].cajas.length - 20} más
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Fibras */}
                        {elementosPorZona[zona.id].fibras.length > 0 && (
                          <div className="elementos-grupo">
                            <div className="grupo-titulo">
                              <span className="grupo-icon">📡</span>
                              Fibras ({elementosPorZona[zona.id].fibras.length})
                            </div>
                            <div className="elementos-lista">
                              {elementosPorZona[zona.id].fibras.slice(0, 10).map((fibra) => (
                                <div
                                  key={fibra.id}
                                  className="elemento-item fibra"
                                  onClick={() => centrarEnFibra(fibra)}
                                >
                                  <span
                                    className="color-indicator"
                                    style={{backgroundColor: obtenerColorFibra(fibra.nombre || fibra.tipo)}}
                                  ></span>
                                  <span className="elemento-nombre">{fibra.nombre || 'Sin nombre'}</span>
                                </div>
                              ))}
                              {elementosPorZona[zona.id].fibras.length > 10 && (
                                <div className="mas-elementos">
                                  ... y {elementosPorZona[zona.id].fibras.length - 10} más
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {elementosPorZona[zona.id].cajas.length === 0 && elementosPorZona[zona.id].fibras.length === 0 && (
                          <div className="sin-elementos">Sin elementos en esta zona</div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="sin-zonas">Cargando zonas...</div>
              )}
            </div>
          </div>

          {/* Búsqueda por Coordenadas */}
          <div className="sidebar-section">
            <h3>Buscar Coordenadas</h3>
            <label>Latitud (ej: -2.1894)</label>
            <input
              type="number"
              step="any"
              value={latitudBusqueda}
              onChange={(e) => setLatitudBusqueda(e.target.value)}
              placeholder="-2.1894"
            />
            <label>Longitud (ej: -79.8890)</label>
            <input
              type="number"
              step="any"
              value={longitudBusqueda}
              onChange={(e) => setLongitudBusqueda(e.target.value)}
              placeholder="-79.8890"
            />
            <button className="btn-buscar" onClick={buscarCoordenadas}>
              Buscar
            </button>
            <button className="btn-limpiar" onClick={limpiarBusqueda}>
              Limpiar
            </button>
          </div>

          {/* Medición de Distancias */}
          <div className="sidebar-section">
            <h3>Medir Distancia</h3>
            {!midiendoDistancia ? (
              <button className="btn-iniciar-medicion" onClick={iniciarMedicion}>
                Iniciar Medición
              </button>
            ) : (
              <>
                <div className="medicion-activa">
                  <span className="medicion-badge">MIDIENDO</span>
                  <p>Haz clic en cualquier parte del mapa para agregar puntos</p>
                </div>
                <div className="medicion-info">
                  <p><strong>Puntos:</strong> {puntosMedicion.length}</p>
                  <p><strong>Distancia:</strong> {distanciaTotal.toFixed(2)} m</p>
                  {distanciaTotal > 1000 && (
                    <p><strong>En km:</strong> {(distanciaTotal / 1000).toFixed(3)} km</p>
                  )}
                </div>
                <button className="btn-retroceder" onClick={retrocederPunto} disabled={puntosMedicion.length === 0}>
                  Retroceder
                </button>
                <button className="btn-finalizar-medicion" onClick={finalizarMedicion}>
                  Finalizar
                </button>
                <button className="btn-limpiar-medicion" onClick={limpiarMedicion}>
                  Limpiar
                </button>
              </>
            )}
          </div>

          {/* Leyenda de Colores */}
          <div className="sidebar-section">
            <h3>Colores de Fibra</h3>
            <div className="leyenda-colores">
              {Object.entries(COLORES_FIBRA).map(([key, value]) => (
                <div key={key} className="leyenda-item">
                  <span className="leyenda-color" style={{backgroundColor: value.color}}></span>
                  <span className="leyenda-texto">{value.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* MAPA PRINCIPAL */}
        <div className="map-content">
          <MapContainer
            center={centroMapa}
            zoom={zoomMapa}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
          >
            <MapController center={centroMapa} zoom={zoomMapa} />
            <MapClickHandler onClick={manejarClicMapa} activo={midiendoDistancia} />
            <MouseCoordinates onMouseMove={(latlng) => setCoordenadasMouse({ lat: latlng.lat, lng: latlng.lng })} />

            {/* Control de Capas */}
            <LayersControl position="topright">
              <BaseLayer checked name="OpenStreetMap">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap'
                />
              </BaseLayer>
              <BaseLayer name="Satélite (Esri)">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='&copy; Esri'
                />
              </BaseLayer>
              <BaseLayer name="Topográfico">
                <TileLayer
                  url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenTopoMap'
                />
              </BaseLayer>

              <Overlay checked name={`Zonas (${zonasGeojson?.features?.length || 0})`}>
                <>
                  {zonasGeojson && capasActivas.zonas && (
                    <GeoJSON
                      key={`zonas-${JSON.stringify(zonasGeojson)}-${midiendoDistancia}`}
                      data={zonasGeojson}
                      style={obtenerEstiloZona}
                      onEachFeature={onEachZona}
                    />
                  )}
                </>
              </Overlay>

              <Overlay checked name={`Cajas NAP (${cajasGeojson?.features?.length || 0})`}>
                <>
                  {cajasGeojson && capasActivas.cajas && (
                    <GeoJSON
                      key={JSON.stringify(cajasGeojson)}
                      data={cajasGeojson}
                      pointToLayer={(feature, latlng) => {
                        return L.circleMarker(latlng, obtenerEstiloCaja());
                      }}
                      onEachFeature={onEachCaja}
                    />
                  )}
                </>
              </Overlay>

              <Overlay checked name={`Fibra (${fibrasGeojson?.features?.length || 0})`}>
                <>
                  {fibrasGeojson && capasActivas.fibras && (
                    <GeoJSON
                      key={`fibras-${fibrasVersion}-${fibrasGeojson?.features?.length}`}
                      data={fibrasGeojson}
                      style={obtenerEstiloFibra}
                      onEachFeature={onEachFibra}
                    />
                  )}
                </>
              </Overlay>
            </LayersControl>

            {marcadorBusqueda && (
              <Marker position={marcadorBusqueda} icon={searchIcon}>
                <Popup>
                  <strong>Ubicación Buscada</strong><br/>
                  Lat: {marcadorBusqueda[0].toFixed(6)}<br/>
                  Lng: {marcadorBusqueda[1].toFixed(6)}
                </Popup>
              </Marker>
            )}

            {puntosMedicion.length > 0 && (
              <>
                {puntosMedicion.map((punto, index) => (
                  <Marker key={index} position={punto} icon={measureIcon}>
                    <Popup>
                      <strong>Punto {index + 1}</strong><br/>
                      Lat: {punto[0].toFixed(6)}<br/>
                      Lng: {punto[1].toFixed(6)}
                    </Popup>
                  </Marker>
                ))}
                {puntosMedicion.length > 1 && (
                  <Polyline
                    positions={puntosMedicion}
                    color="#FF4444"
                    weight={3}
                    dashArray="10, 5"
                  />
                )}
              </>
            )}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default MapView;
